import type { ScreenerTickerData } from '@/types'

/** Per-view LRU; exact request contexts prevent cross-symbol/market reuse. */
export class ScreenerResponseCache {
  private entries = new Map<string, { data: ScreenerTickerData; at: number; points: number; bytes: number }>()
  constructor(private maxEntries = 8, private maxPoints = 120_000, private maxBytes = 64 * 1024 * 1024) {}
  clear(): void { this.entries.clear() }

  get(key: string, maxAgeMs = 30_000, now = Date.now()): ScreenerTickerData | null {
    const entry = this.entries.get(key)
    if (!entry || now - entry.at > maxAgeMs) return null
    this.entries.delete(key)
    this.entries.set(key, entry)
    return entry.data
  }

  set(key: string, data: ScreenerTickerData, now = Date.now()): void {
    const points = Object.values(data.exchanges).reduce((count, venue) => count
      + (venue.candles?.length || 0) + (venue.trade_price_history?.length || 0)
      + (venue.oi_history?.length || 0) + (venue.volume_history?.length || 0)
      + (venue.cvd_history?.length || 0), 0)
    // Sample point shapes so retaining a rolling week never serializes the
    // complete response on the main thread. The separate point cap bounds it.
    let bytes = 1024
    for (const venue of Object.values(data.exchanges)) {
      bytes += 2048
      for (const series of [venue.candles, venue.trade_price_history, venue.oi_history, venue.volume_history, venue.cvd_history]) {
        if (!series?.length) continue
        const samples = [series[0], series[Math.floor(series.length / 2)], series[series.length - 1]]
        bytes += series.length * (64 + Math.max(...samples.map(point => JSON.stringify(point).length * 2)))
      }
    }
    this.entries.delete(key)
    if (points > this.maxPoints || bytes > this.maxBytes) return
    this.entries.set(key, { data, at: now, points, bytes })
    while (this.entries.size > this.maxEntries
      || [...this.entries.values()].reduce((sum, entry) => sum + entry.points, 0) > this.maxPoints
      || [...this.entries.values()].reduce((sum, entry) => sum + entry.bytes, 0) > this.maxBytes) {
      this.entries.delete(this.entries.keys().next().value!)
    }
  }
}
